import { useCallback } from 'react';
import { ensureDestinationAlive } from '../graphNodes';
import { stopAll } from '../bufferTransport';
import { disposeStems } from './refs';

interface Args {
  refs: any;
  bufferModeRef: React.MutableRefObject<boolean>;
  scheduledRef: React.MutableRefObject<Record<string, any> | null>;
  logicalStartRef: React.MutableRefObject<number>;
  startedAtRef: React.MutableRefObject<number>;
  masterPlaybackRate: number;
  pauseAll: () => void;
  setStatus: (s: 'idle' | 'loading' | 'playing' | 'paused') => void;
  setCurrentTime: (t: number) => void;
  resumeAfterLoadRef: React.MutableRefObject<boolean>;
  seekingRef: React.MutableRefObject<{ runId: number; target: number } | null>;
  // NOTE: do NOT use close/open gate here; seeking logic controls the gate.
  userPausedRef: React.MutableRefObject<boolean>;
}

/**
 * Isolated transport controls for Pause/Stop with deterministic silence and
 * correct interaction with both buffer and element paths.
 */
export function useTransportControls({
  refs,
  bufferModeRef,
  scheduledRef,
  logicalStartRef,
  startedAtRef,
  masterPlaybackRate,
  pauseAll,
  setStatus,
  setCurrentTime,
  resumeAfterLoadRef,
  seekingRef,
  userPausedRef,
}: Args) {
  const pause = useCallback(() => {
  // Reflect UI state immediately
  setStatus('paused');
    // Mark explicit user pause to block auto-resume gates
    try { userPausedRef.current = true; } catch {}
  // Element path removed; pauseAll retained as no-op safety.
  try { pauseAll(); } catch {}
  // Prevent future auto-resume-on-load while we're paused
  try { resumeAfterLoadRef.current = false; } catch {}
  // Cancel any in-flight seek that could resume playback
  try { seekingRef.current = null; } catch {}
    // If buffer transport is active, stop sources but preserve timeline
  if (bufferModeRef.current && scheduledRef.current && refs.audioCtxRef.current) {
      const ctx: AudioContext = refs.audioCtxRef.current;
      const elapsed = Math.max(0, ctx.currentTime - startedAtRef.current) * masterPlaybackRate;
  logicalStartRef.current = (logicalStartRef.current + elapsed);
  // lock the new timeline origin so subsequent resumes don’t double-count
  startedAtRef.current = ctx.currentTime;
      try { stopAll(scheduledRef.current); } catch {}
    }
    // Anchor UI to the paused timeline position immediately
    try { setCurrentTime(logicalStartRef.current); } catch {}
    // Safety: force transport gate (or master) to 0 to guarantee silence
    try {
      const ctx: AudioContext | null = refs.audioCtxRef.current;
      const now = ctx?.currentTime ?? 0;
      const gateNode: GainNode | null = (refs.gateGainRef?.current ?? refs.masterGainRef?.current) || null;
      const g: AudioParam | null = (gateNode && (gateNode as any).gain) ? (gateNode as any).gain as AudioParam : null;
      if (g) {
        try { g.cancelScheduledValues(now); } catch {}
        try { g.setValueAtTime(0, now); } catch { try { (g as any).value = 0; } catch {} }
      }
    } catch {}
    // Affirm destination hop after pausing to avoid silent resume
    try {
      const ctx = refs.audioCtxRef.current as AudioContext | null;
      if (ctx) ensureDestinationAlive(ctx, { masterGainRef: refs.masterGainRef, masterHighpassRef: refs.masterHighpassRef, masterFilterRef: refs.masterFilterRef });
    } catch {}
    // DO NOT suspend AudioContext here; play() is responsible for resume/open.
  }, [pauseAll, bufferModeRef, scheduledRef, startedAtRef, masterPlaybackRate, logicalStartRef, setStatus, userPausedRef, resumeAfterLoadRef, seekingRef]);

  const stop = useCallback(() => {
  // Reflect UI state immediately
  setStatus('idle');
  setCurrentTime(0);
  // Treat Stop as a user-controlled halt to block any auto-resume gates until Play
  try { userPausedRef.current = true; } catch {}
  try { pauseAll(); } catch {}
    // Reset transport clock anchor so next Play starts from logicalStart (0)
    try {
      (refs as any).transportClockRef = (refs as any).transportClockRef || { current: null };
      (refs as any).transportClockRef.current = null;
    } catch {}
    // Hard kill-switch: cancel any pending gate ramps and force gain to 0 NOW
    try {
      const ctx: AudioContext | null = refs.audioCtxRef.current;
      const now = ctx?.currentTime ?? 0;
      const gateNode: GainNode | null = (refs.gateGainRef?.current ?? refs.masterGainRef?.current) || null;
      const g: AudioParam | null = (gateNode && (gateNode as any).gain) ? (gateNode as any).gain as AudioParam : null;
      if (g) {
        try { g.cancelScheduledValues(now); } catch {}
        try { g.setValueAtTime(0, now); } catch { try { (g as any).value = 0; } catch {} }
      }
      // Also immediately suspend the AudioContext to guarantee silence regardless of graph state
      try { if (ctx && ctx.state === 'running') { ctx.suspend().catch(() => {}); } } catch {}
      // Cancel any active post-start gate probes so they can't auto-open after Stop
      try { (refs as any)._gateProbeActive = false; } catch {}
      try { (refs as any)._gateProbeOwnerId = undefined; } catch {}
      try { (refs as any)._postProbeId = ((refs as any)._postProbeId ?? 0) + 1; } catch {}
    } catch {}
    // Reset micro-delays to 0 and restore per-stem gains to 1
    try {
      const delays = refs.delaysRef.current as Record<string, DelayNode | undefined>;
      const gains = refs.gainsRef.current as Record<string, GainNode | undefined>;
      const now = refs.audioCtxRef.current?.currentTime ?? 0;
      for (const role of Object.keys(delays)) {
        const d = delays[role]; if (!d) continue;
        try { d.delayTime.cancelScheduledValues(now); } catch {}
        try { d.delayTime.setValueAtTime(0, now); } catch {}
      }
      for (const role of Object.keys(gains)) {
        const g = gains[role]; if (!g) continue;
        try { g.gain.cancelScheduledValues(now); } catch {}
        try { g.gain.setValueAtTime(1, now); } catch {}
      }
    } catch {}
  // Stop buffer transport and reset logical timeline
    if (bufferModeRef.current && scheduledRef.current) {
      try { stopAll(scheduledRef.current); } catch {}
    }
    // Dispose any dangling per-stem nodes immediately to prevent freezes/leaks
    try {
      const ctx = refs.audioCtxRef.current as AudioContext | null;
      if (ctx) disposeStems(ctx, { sourcesRef: refs.sourcesRef, gainsRef: refs.gainsRef, analyzersRef: refs.analyzersRef, delaysRef: refs.delaysRef });
    } catch {}
  logicalStartRef.current = 0;
    startedAtRef.current = refs.audioCtxRef.current?.currentTime ?? 0;
  // Clear scheduled references to reflect fully stopped state
  try { scheduledRef.current = null; } catch {}
  // Prevent auto-resume after future loads; keep analyzers map intact so UI can freeze gracefully
  // (levels hook sets floor snapshot when status === 'idle').
  resumeAfterLoadRef.current = false;
    // Clear any active seek session
    seekingRef.current = null;
  // As a final guard, suspend the AudioContext so no WebAudio path leaks sound
  try { if (refs.audioCtxRef.current && refs.audioCtxRef.current.state === 'running') { refs.audioCtxRef.current.suspend().catch(()=>{}); } } catch {}
    // Re-affirm destination hop to be safe if user hits Play quickly after Stop
    try {
      const ctx = refs.audioCtxRef.current as AudioContext | null;
      if (ctx) ensureDestinationAlive(ctx, { masterGainRef: refs.masterGainRef, masterHighpassRef: refs.masterHighpassRef, masterFilterRef: refs.masterFilterRef });
    } catch {}
    // Do NOT suspend AudioContext here; play() will resume as needed.
    // Visuals clear themselves when status === 'idle'.
  }, [pauseAll, bufferModeRef, scheduledRef, logicalStartRef, startedAtRef, resumeAfterLoadRef, seekingRef, setCurrentTime, setStatus, userPausedRef]);

  return { pause, stop } as const;
}
